// ============================================
// DISTANCE MATRIX UTILITY
// ============================================

export interface DistanceMatrixResult {
  distanceKm: number;
  durationMinutes: number;
  durationText: string;
  distanceText: string;
}

/**
 * Calculate distance and travel time between two addresses using Google Maps Distance Matrix API.
 * Returns one-way distance and time - multiply by tripMultiplier for round-trip.
 * 
 * @param origin - Starting address (e.g., service branch)
 * @param destination - Destination address (e.g., customer site)
 * @returns Promise with distance in km and duration in minutes
 */
export async function calculateDistance(
  origin: string,
  destination: string
): Promise<DistanceMatrixResult> {
  return new Promise((resolve, reject) => {
    if (!window.google?.maps?.DistanceMatrixService) {
      reject(new Error('Google Maps not loaded'));
      return;
    }

    const service = new google.maps.DistanceMatrixService();
    service.getDistanceMatrix(
      {
        origins: [origin],
        destinations: [destination],
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status === 'OK' && response) {
          const element = response.rows[0]?.elements[0];
          if (element?.status === 'OK') {
            resolve({
              distanceKm: Math.round(element.distance.value / 1000),
              durationMinutes: Math.round(element.duration.value / 60),
              distanceText: element.distance.text,
              durationText: element.duration.text,
            });
          } else {
            reject(new Error('No route found'));
          }
        } else {
          reject(new Error(`Distance Matrix error: ${status}`));
        }
      }
    );
  });
}
